import React, { useState } from 'react';
import { Search, X, Plus } from 'lucide-react';

const initials = (name = '') => name.trim().split(/\s+/).slice(0, 2).map(w => w[0]).join('').toUpperCase();

const ItemIcon = ({ item, shape, size }) => {
  if (shape === 'dot') {
    return <span className="link-picker-dot" style={{ width: size, height: size, background: item.color || 'hsl(var(--primary))' }} />;
  }
  if (item.photo_url) {
    return <img src={item.photo_url} alt={item.name} className="link-picker-avatar"
      style={{ width: size, height: size }} />;
  }
  return (
    <span className="link-picker-avatar link-picker-initials"
      style={{ width: size, height: size, background: item.color || 'hsl(var(--primary))', fontSize: Math.round(size * 0.42) }}>
      {initials(item.name)}
    </span>
  );
};

/**
 * Search-and-add picker used for every "Assign X" field (members, projects,
 * clients). Values are always stored as an array of ids, even when single.
 */
export const LinkPickerField = ({
  label, items = [], value, onChange, multiple = false,
  shape = 'avatar', noun = 'item', testid, labelExtra, fullWidth = true,
}) => {
  const [search, setSearch] = useState('');
  const selectedIds = Array.isArray(value) ? value : value ? [value] : [];
  const selected = selectedIds.map(id => items.find(i => i.id === id)).filter(Boolean);
  const remaining = items.filter(i => !selectedIds.includes(i.id));
  const query = search.trim().toLowerCase();
  const list = query ? remaining.filter(i => i.name.toLowerCase().includes(query)) : remaining;

  const add = (id) => { onChange(multiple ? [...selectedIds, id] : [id]); setSearch(''); };
  const remove = (id) => onChange(selectedIds.filter(x => x !== id));

  return (
    <div className={`form-field${fullWidth ? ' job-field-full' : ''}`}>
      <label className="form-label">{label}{labelExtra}</label>
      <div className="assign-members-field" data-testid={testid}>
        {selected.length > 0 && (
          <div className="member-chips-row">
            {selected.map(item => (
              <div key={item.id} className="member-chip-assigned" data-testid={`${testid}-chip-${item.id}`}>
                <ItemIcon item={item} shape={shape} size={20} />
                <span>{item.name}</span>
                <button type="button" className="member-chip-remove" onClick={() => remove(item.id)}
                  data-testid={`${testid}-remove-${item.id}`}><X size={11} /></button>
              </div>
            ))}
          </div>
        )}
        <div className="member-search-row">
          <Search size={13} className="member-search-icon" />
          <input className="member-search-input" value={search}
            placeholder={selected.length && !multiple ? `Change ${noun}...` : `Search ${noun}s...`}
            onChange={e => setSearch(e.target.value)}
            data-testid={`${testid}-search`} />
          {search && (
            <button type="button" className="member-chip-remove" onClick={() => setSearch('')}>
              <X size={12} />
            </button>
          )}
        </div>
        {list.length ? (
          <div className="member-add-list">
            {list.map(item => (
              <button key={item.id} type="button" className="member-add-row"
                onClick={() => add(item.id)} data-testid={`${testid}-add-${item.id}`}>
                <ItemIcon item={item} shape={shape} size={24} />
                <div className="member-add-info">
                  <span className="member-add-name">{item.name}</span>
                  {item.subtitle && <span className="member-add-role">{item.subtitle}</span>}
                </div>
                <Plus size={13} className="member-add-plus" />
              </button>
            ))}
          </div>
        ) : (
          <p className="member-chip-empty" data-testid={`${testid}-empty`}>
            {query ? `No ${noun}s found` : `No ${noun}s available`}
          </p>
        )}
      </div>
    </div>
  );
};
